import type { PowerDocSection } from '@/lib/power-doc-section';

/** Labels discretos para capítulos narrativos. */
export const POWER_LORE_CHAPTER_LABELS = [
  { id: '', label: 'Sem label' },
  { id: 'ORIGEM', label: 'Origem' },
  { id: 'HISTÓRIA', label: 'História' },
  { id: 'LEGADO', label: 'Legado' },
  { id: 'CONTEXTO', label: 'Contexto' },
] as const;

export type PowerLoreChapterLabel = (typeof POWER_LORE_CHAPTER_LABELS)[number]['id'];

/** Modelos visuais da Lore (só acentos; o fundo permanece azul escuro). */
export const POWER_LORE_THEME_IDS = ['normal', 'rare', 'epic'] as const;
export type PowerLoreThemeId = (typeof POWER_LORE_THEME_IDS)[number];

export type PowerLoreThemeStyle = {
  id: PowerLoreThemeId;
  label: string;
  /** Cor da bolinha no editor. */
  swatch: string;
  /** RGB «r, g, b» para gradientes do separador cristal. */
  accentRgb: string;
  eyebrow: string;
  chapterMeta: string;
  badge: string;
  timeline: string;
  dot: string;
  dotHalo: string;
  smileyGlow: string;
  smileyFrame: string;
  topHairline: string;
  cardBorder: string;
  cardRing: string;
  cardShadow: string;
  orbPrimary: string;
  orbSecondary: string;
  mention: string;
  richLink: string;
};

export const POWER_LORE_THEMES: Record<PowerLoreThemeId, PowerLoreThemeStyle> = {
  normal: {
    id: 'normal',
    label: 'Normal',
    swatch: '#38bdf8',
    accentRgb: '56, 189, 248',
    eyebrow: 'text-sky-400/80',
    chapterMeta: 'text-sky-400/70',
    badge: 'border-sky-400/20 bg-sky-400/10 text-sky-300/90',
    timeline: 'from-sky-400/50 via-sky-500/25 to-transparent',
    dot: 'bg-sky-400/90 shadow-[0_0_12px_2px_rgba(56,189,248,0.55)]',
    dotHalo: 'bg-sky-400/15',
    smileyGlow: 'bg-sky-400/15',
    smileyFrame:
      'border-sky-400/25 bg-gradient-to-b from-sky-400/15 to-transparent shadow-[0_0_40px_-8px_rgba(56,189,248,0.55)]',
    topHairline: 'via-sky-400/40',
    cardBorder: 'border-sky-500/20',
    cardRing: 'ring-sky-400/10',
    cardShadow: 'shadow-[0_0_60px_-20px_rgba(14,165,233,0.35)]',
    orbPrimary: 'bg-sky-500/10',
    orbSecondary: 'bg-cyan-400/10',
    mention:
      '!text-sky-300 decoration-sky-400/40 hover:!text-sky-200 focus-visible:ring-sky-400/50',
    richLink: '[&_a]:!text-sky-300 hover:[&_a]:!text-sky-200 [&_strong]:text-sky-50',
  },
  rare: {
    id: 'rare',
    label: 'Rara',
    swatch: '#34d399',
    accentRgb: '52, 211, 153',
    eyebrow: 'text-emerald-300',
    chapterMeta: 'text-emerald-300/80',
    badge: 'border-emerald-400/25 bg-emerald-400/10 text-emerald-200/90',
    timeline: 'from-emerald-400/50 via-emerald-500/25 to-transparent',
    dot: 'bg-emerald-400/90 shadow-[0_0_12px_2px_rgba(52,211,153,0.55)]',
    dotHalo: 'bg-emerald-400/15',
    smileyGlow: 'bg-emerald-400/15',
    smileyFrame:
      'border-emerald-400/30 bg-gradient-to-b from-emerald-400/15 to-transparent shadow-[0_0_40px_-8px_rgba(52,211,153,0.5)]',
    topHairline: 'via-emerald-400/40',
    cardBorder: 'border-emerald-500/20',
    cardRing: 'ring-emerald-400/10',
    cardShadow: 'shadow-[0_0_60px_-20px_rgba(16,185,129,0.3)]',
    orbPrimary: 'bg-emerald-500/10',
    orbSecondary: 'bg-teal-400/10',
    mention:
      '!text-emerald-300 decoration-emerald-400/40 hover:!text-emerald-200 focus-visible:ring-emerald-400/50',
    richLink: '[&_a]:!text-emerald-300 hover:[&_a]:!text-emerald-200 [&_strong]:text-emerald-50',
  },
  epic: {
    id: 'epic',
    label: 'Épica',
    swatch: '#fbbf24',
    accentRgb: '251, 191, 36',
    eyebrow: 'text-amber-300',
    chapterMeta: 'text-amber-300/80',
    badge: 'border-amber-400/25 bg-amber-400/10 text-amber-200/90',
    timeline: 'from-amber-400/50 via-amber-500/25 to-transparent',
    dot: 'bg-amber-400/90 shadow-[0_0_12px_2px_rgba(251,191,36,0.55)]',
    dotHalo: 'bg-amber-400/15',
    smileyGlow: 'bg-amber-400/15',
    smileyFrame:
      'border-amber-400/30 bg-gradient-to-b from-amber-400/15 to-transparent shadow-[0_0_40px_-8px_rgba(251,191,36,0.5)]',
    topHairline: 'via-amber-400/40',
    cardBorder: 'border-amber-500/20',
    cardRing: 'ring-amber-400/10',
    cardShadow: 'shadow-[0_0_60px_-20px_rgba(245,158,11,0.3)]',
    orbPrimary: 'bg-amber-500/10',
    orbSecondary: 'bg-yellow-400/10',
    mention:
      '!text-amber-300 decoration-amber-400/40 hover:!text-amber-200 focus-visible:ring-amber-400/50',
    richLink: '[&_a]:!text-amber-300 hover:[&_a]:!text-amber-200 [&_strong]:text-amber-50',
  },
};

export function isPowerLoreThemeId(v: unknown): v is PowerLoreThemeId {
  return typeof v === 'string' && (POWER_LORE_THEME_IDS as readonly string[]).includes(v);
}

export function getPowerLoreTheme(
  id: string | undefined | null,
  fallback: PowerLoreThemeId = 'normal'
): PowerLoreThemeStyle {
  if (isPowerLoreThemeId(id)) return POWER_LORE_THEMES[id];
  return POWER_LORE_THEMES[fallback];
}

export type PowerLoreChapter = {
  id: string;
  /** Label discreto (ex.: ORIGEM) — opcional. */
  label: string;
  title: string;
  /** HTML rich text. */
  body: string;
};

export type PowerLoreCuriosity = {
  id: string;
  /** Ex.: «Você sabia?» — vazio usa o default. */
  title: string;
  /** HTML ou texto simples. */
  body: string;
  /** Smiley opcional (sem parênteses) ou path `/icons/….svg`. */
  smiley: string;
};

export type PowerLore = {
  /** Se false, a secção não é publicada (mesmo com conteúdo). */
  enabled: boolean;
  /** Subtítulo pequeno acima do título (ex.: «Nos arquivos do xat»). */
  subtitle: string;
  /** Título principal configurável no CMS. */
  title: string;
  /** Smiley em destaque (sem parênteses). */
  smiley: string;
  /** Introdução narrativa (HTML). */
  intro: string;
  /** Modelo visual: Normal (azul), Rara (verde), Épica (âmbar). */
  theme: PowerLoreThemeId;
  chapters: PowerLoreChapter[];
  curiosities: PowerLoreCuriosity[];
};

function newLoreId(prefix: string): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyPowerLoreChapter(
  overrides?: Partial<Omit<PowerLoreChapter, 'id'>> & { id?: string }
): PowerLoreChapter {
  return {
    id: overrides?.id ?? newLoreId('lore-ch'),
    label: overrides?.label ?? '',
    title: overrides?.title ?? '',
    body: overrides?.body ?? '',
  };
}

export function emptyPowerLoreCuriosity(
  overrides?: Partial<Omit<PowerLoreCuriosity, 'id'>> & { id?: string }
): PowerLoreCuriosity {
  return {
    id: overrides?.id ?? newLoreId('lore-cu'),
    title: overrides?.title ?? '',
    body: overrides?.body ?? '',
    smiley: overrides?.smiley ?? '',
  };
}

export function emptyPowerLore(overrides?: Partial<PowerLore>): PowerLore {
  return {
    enabled: overrides?.enabled ?? false,
    subtitle: overrides?.subtitle ?? '',
    title: overrides?.title ?? '',
    smiley: overrides?.smiley ?? '',
    intro: overrides?.intro ?? '',
    theme: overrides?.theme ?? 'normal',
    chapters: overrides?.chapters ? [...overrides.chapters] : [],
    curiosities: overrides?.curiosities ? [...overrides.curiosities] : [],
  };
}

/** Conteúdo inicial ao ativar a Lore pela primeira vez no editor. */
export function seedPowerLoreForEditor(opts: {
  powerName: string;
  powerSlug: string;
  defaultSmiley?: string;
  theme?: PowerLoreThemeId;
}): PowerLore {
  const name = opts.powerName.trim();
  const smiley = (opts.defaultSmiley || opts.powerSlug || 'smile').replace(/[()]/g, '').trim();
  return emptyPowerLore({
    enabled: true,
    theme: opts.theme ?? 'normal',
    subtitle: 'Nos arquivos do xat',
    title: name ? `A história por trás do ${name}` : 'A história por trás do Power',
    smiley,
    chapters: [
      emptyPowerLoreChapter({
        label: 'HISTÓRIA',
        title: 'O começo',
        body: '<p>Conte aqui a origem deste power — quando surgiu, o que mudou e por que ficou marcado na história do xat. Mencione outros powers com [[slug]] se quiser.</p>',
      }),
    ],
  });
}

function normalizeChapter(raw: unknown): PowerLoreChapter | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const title = typeof o.title === 'string' ? o.title : '';
  const body =
    typeof o.body === 'string' ? o.body : typeof o.description === 'string' ? o.description : '';
  const label = typeof o.label === 'string' ? o.label : '';
  const id = typeof o.id === 'string' && o.id.trim() ? o.id.trim() : newLoreId('lore-ch');
  return { id, label, title, body };
}

function normalizeCuriosity(raw: unknown): PowerLoreCuriosity | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const title = typeof o.title === 'string' ? o.title : '';
  const body = typeof o.body === 'string' ? o.body : typeof o.text === 'string' ? o.text : '';
  const smiley = typeof o.smiley === 'string' ? o.smiley.trim() : '';
  const id = typeof o.id === 'string' && o.id.trim() ? o.id.trim() : newLoreId('lore-cu');
  return { id, title, body, smiley };
}

export function normalizePowerLore(raw: unknown): PowerLore {
  if (!raw || typeof raw !== 'object') return emptyPowerLore();
  const o = raw as Record<string, unknown>;
  const chapters = Array.isArray(o.chapters)
    ? o.chapters.map(normalizeChapter).filter((c): c is PowerLoreChapter => Boolean(c))
    : [];
  const curiosities = Array.isArray(o.curiosities)
    ? o.curiosities.map(normalizeCuriosity).filter((c): c is PowerLoreCuriosity => Boolean(c))
    : [];
  return {
    enabled: Boolean(o.enabled),
    subtitle: typeof o.subtitle === 'string' ? o.subtitle : '',
    title: typeof o.title === 'string' ? o.title : '',
    smiley: typeof o.smiley === 'string' ? o.smiley.replace(/[()]/g, '').trim() : '',
    intro: typeof o.intro === 'string' ? o.intro : '',
    theme: isPowerLoreThemeId(o.theme) ? o.theme : 'normal',
    chapters,
    curiosities,
  };
}

export function powerLoreForPersist(lore: PowerLore): PowerLore | undefined {
  const chapters = lore.chapters
    .map((c) => ({
      id: c.id,
      label: c.label.trim(),
      title: c.title.trim(),
      body: c.body.trim(),
    }))
    .filter((c) => c.title || c.body);
  const curiosities = lore.curiosities
    .map((c) => ({
      id: c.id,
      title: c.title.trim(),
      body: c.body.trim(),
      smiley: c.smiley.trim(),
    }))
    .filter((c) => c.body || c.title);
  const next: PowerLore = {
    enabled: lore.enabled,
    subtitle: lore.subtitle.trim(),
    title: lore.title.trim(),
    smiley: lore.smiley.replace(/[()]/g, '').trim(),
    intro: lore.intro.trim(),
    theme: isPowerLoreThemeId(lore.theme) ? lore.theme : 'normal',
    chapters,
    curiosities,
  };
  if (
    !next.enabled &&
    !next.subtitle &&
    !next.title &&
    !next.intro &&
    chapters.length === 0 &&
    curiosities.length === 0
  ) {
    return undefined;
  }
  return next;
}

/** Há conteúdo suficiente para renderizar a experiência de Lore. */
export function powerLoreHasRenderableContent(lore: PowerLore | null | undefined): boolean {
  if (!lore) return false;
  if (!lore.enabled) return false;
  return Boolean(
    lore.title.trim() ||
      lore.subtitle.trim() ||
      lore.intro.trim() ||
      lore.chapters.some((c) => c.title.trim() || c.body.trim()) ||
      lore.curiosities.some((c) => c.body.trim() || c.title.trim())
  );
}

/** Heurística: secção wiki que parece narrativa / história (ex.: Topman do Cupim). */
export function isLikelyLoreDocSection(section: PowerDocSection): boolean {
  const hay = `${section.subtitle} ${section.title}`
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
  return /historia|origem|lore|legado|onda|antes|curiosidade|ha muito|contexto|arquivo|geracao|primeira/.test(
    hay
  );
}

export function loreFromDocSections(
  sections: PowerDocSection[],
  opts?: { powerName?: string; powerSlug?: string; smiley?: string }
): PowerLore {
  const chapters = sections.map((s, index) =>
    emptyPowerLoreChapter({
      id: s.id || newLoreId('lore-ch'),
      label: index === 0 ? 'HISTÓRIA' : s.subtitle.trim().toUpperCase() || '',
      title: s.title.trim(),
      body: s.description,
    })
  );
  const name = opts?.powerName?.trim() || opts?.powerSlug?.trim() || 'este Power';
  return emptyPowerLore({
    enabled: true,
    theme: 'normal',
    subtitle: 'Nos arquivos do xat',
    title: `A história por trás do ${name}`,
    smiley: (opts?.smiley || opts?.powerSlug || 'smile').replace(/[()]/g, '').trim(),
    intro: '',
    chapters,
    curiosities: [],
  });
}

export type PowerLoreSource = {
  lore?: PowerLore | null;
  name?: string;
  slug?: string;
  smilies?: string[];
  docSectionsEnabled?: boolean;
  docSections?: PowerDocSection[];
};

/**
 * Move secções wiki «de lore» para o campo `lore` e remove-as de `docSections`.
 * Idempotente: se a Lore já tem conteúdo, só limpa os leftovers wiki.
 */
export function migratePowerLoreFields<T extends PowerLoreSource>(power: T): T {
  const sections = power.docSections ?? [];
  const stored = normalizePowerLore(power.lore);

  if (!sections.length) {
    return { ...power, lore: stored };
  }

  const afterOverview = sections.filter((s) => s.position === 'after-overview');
  const likely = afterOverview.filter(isLikelyLoreDocSection);
  const candidates =
    likely.length > 0
      ? likely
      : power.slug?.toLowerCase() === 'topman' &&
          afterOverview.length > 0 &&
          !powerLoreHasRenderableContent(stored)
        ? afterOverview
        : [];

  if (candidates.length === 0) {
    return { ...power, lore: stored };
  }

  const candidateIds = new Set(candidates.map((c) => c.id));
  const remaining = sections.filter((s) => !candidateIds.has(s.id));

  const lore = powerLoreHasRenderableContent(stored)
    ? stored
    : loreFromDocSections(candidates, {
        powerName: power.name,
        powerSlug: power.slug,
        smiley:
          stored.smiley ||
          power.smilies?.[0]?.replace(/[()]/g, '').trim() ||
          power.slug ||
          'smile',
      });

  return {
    ...power,
    lore,
    docSections: remaining,
    docSectionsEnabled: remaining.length > 0 ? Boolean(power.docSectionsEnabled) : false,
  };
}

/**
 * Resolve a Lore a mostrar na página pública.
 */
export function resolvePowerLore(power: PowerLoreSource): {
  lore: PowerLore | null;
  bridgedDocSectionIds: string[];
} {
  const migrated = migratePowerLoreFields(power);
  const stored = normalizePowerLore(migrated.lore);

  if (powerLoreHasRenderableContent(stored)) {
    const leftoverIds = (power.docSections ?? [])
      .filter((s) => s.position === 'after-overview' && isLikelyLoreDocSection(s))
      .map((s) => s.id);
    return { lore: stored, bridgedDocSectionIds: leftoverIds };
  }

  return { lore: null, bridgedDocSectionIds: [] };
}

export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (from < 0 || to < 0 || from >= list.length || to >= list.length || from === to) {
    return list;
  }
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
